import _ from "lodash";

import { TemplateType, templateMaxItems } from "./constants";
import { OrderType } from "../../search/utils/constants";
import { MAX_ORDERS } from "../../../../homologar/orders/utils/constants";

const mapItems = (items) => items.map((item) => item.name);
const mapItemsAndJoin = (items, join) => mapItems(items).join(join);

export const extractItems = (template, orderType) => {
  if (!template) {
    return [];
  }

  const { inputMethod } = template;
  return contentMapper[orderType][inputMethod].extractItems(
    template,
    orderType
  );
};

// Story 8: la API arma/hidrata `content` (id|id) — el front manda/lee `items`
// ({procedureId} | {text}), sin joins/splits propios. Igual para TEXT y SET
// (inputMethod solo controla presentación, no la forma de los datos).
const homologarMapItemsToContent = (items) => ({
  items: items.map((item) =>
    item.procedureId != null ? { procedureId: item.procedureId } : { text: item.name }
  ),
});

// Lleva los `selectedItems` del diálogo (forma {name, procedureId?, procedureType?})
// a la MISMA forma que hidrata el GET de la API ({procedureId, name, procedureType} |
// {text}). Así el template en memoria tras crear/editar se comporta igual que
// recargado (extractItems lee item.text para los legacy) — sin esto los nombres se
// ven vacíos hasta recargar.
export const toHydratedItems = (items = []) =>
  items.map((item) =>
    item.procedureId != null
      ? {
          procedureId: item.procedureId,
          name: item.name,
          procedureType: item.procedureType,
          chapter: item.chapter,
        }
      : { text: item.name }
  );

const homologarExtractItems = (template) => {
  if (!template || !template.items) {
    return [];
  }
  return template.items.map((item) =>
    item.procedureId != null
      ? {
          procedureId: item.procedureId,
          name: item.name,
          procedureType: item.procedureType,
          chapter: item.chapter,
        }
      : { name: item.text }
  );
};

const homologarContentMapper = {
  [TemplateType.TEXT]: {
    mapItemsToContent: homologarMapItemsToContent,
    extractItems: homologarExtractItems,
  },
  [TemplateType.SET]: {
    mapItemsToContent: homologarMapItemsToContent,
    extractItems: homologarExtractItems,
  },
};

const recetarioContentMapper = {
  [TemplateType.TEXT]: {
    mapItemsToContent: (items) => ({
      content: mapItemsAndJoin(items, "\n"),
    }),
    extractItems: (template) => {
      if (!template) {
        return [];
      }
      // Una plantilla homologar (con `items`, sin `content`) puede llegar acá si el
      // orderType no coincide con su tipo — no hay nada que separar.
      const { content } = template;
      if (!content) {
        return [];
      }
      return content.split("\n").map((item) => ({ name: item }));
    },
  },
  [TemplateType.SET]: {
    mapItemsToContent: (items) => ({
      content: mapItemsAndJoin(items, ","),
      contents: mapItems(items),
    }),
    extractItems: (template) => {
      if (!template) {
        return [];
      }
      const { content } = template;
      if (!content) {
        return [];
      }
      return content.split(",").map((item) => ({ name: item }));
    },
  },
};

export const contentMapper = {
  [OrderType.MANUAL]: recetarioContentMapper,
  [OrderType.LABORATORY]: homologarContentMapper,
  [OrderType.IMAGING]: homologarContentMapper,
  // Story 8: con el tipo unificado (order-homologar), extractItems ya no puede
  // derivar imaging/laboratory desde template.type — el fallback en
  // extractItems() termina usando el orderType del contexto tal cual, que para
  // la pestaña "Prácticas" es PRACTICES. Sin esta entrada, contentMapper[PRACTICES]
  // da undefined y explota al indexar [inputMethod] después.
  [OrderType.PRACTICES]: homologarContentMapper,
};

const templateType = "order";

// Story 8 (2026-08-07): imaging y laboratory comparten un solo tipo de plantilla
// nuevo, "order-homologar" — no se puede distinguir imaging/laboratory de forma
// confiable al crear desde "Prácticas" (único contexto real, incluso en v2 hoy),
// y cada ítem ya sabe su propio tipo por su procedureId, así que la plantilla
// entera no necesita tenerlo. `HOMOLOGAR_TYPE` es lo que se manda al crear.
const HOMOLOGAR_TYPE = `${templateType}-homologar`;

export const templateTypeMapper = {
  [OrderType.MANUAL]: `${templateType}-${OrderType.MANUAL}`,
  [OrderType.LABORATORY]: HOMOLOGAR_TYPE,
  [OrderType.IMAGING]: HOMOLOGAR_TYPE,
};

// Tipos legacy — plantillas viejas (v1/v2 no migradas a la unificación): siguen
// guardadas como order-imaging / order-laboratory.
const LEGACY_HOMOLOGAR_TYPES = [`${templateType}-${OrderType.IMAGING}`, `${templateType}-${OrderType.LABORATORY}`];

/**
 * Returns the template type string(s) needed for API calls given an orderType.
 * - v3 (ej. IAF): solo el tipo unificado `order-homologar` (ya migrado).
 * - legacy por pestaña: v1 tabbed trae SOLO el tipo de la pestaña (imaging o
 *   laboratory) — así una plantilla de imágenes no aparece en la de laboratorio;
 *   v2 unificada ("Prácticas") trae ambos.
 * - manual: siempre order-manual (su propia pestaña).
 */
export const getTemplateTypes = (orderType, isNomenclatureV3) => {
  if (orderType === OrderType.MANUAL) {
    return templateTypeMapper[orderType];
  }
  if (isNomenclatureV3) {
    return HOMOLOGAR_TYPE;
  }
  if (orderType === OrderType.IMAGING) return `${templateType}-${OrderType.IMAGING}`;
  if (orderType === OrderType.LABORATORY) return `${templateType}-${OrderType.LABORATORY}`;
  return LEGACY_HOMOLOGAR_TYPES;
};

/**
 * Tipo con el que se GUARDA una plantilla al crearla, según la versión — consistente
 * con getTemplateTypes (lo que se guarda es lo que después se trae):
 * - v3: order-homologar (unificado; cada ítem sabe su tipo por su procedureId).
 * - legacy (v1/v2): order-imaging / order-laboratory según el tipo (bloqueado al 1er ítem).
 * - manual: order-manual.
 */
export const getTemplateCreateType = (resolvedOrderType, isNomenclatureV3) => {
  if (resolvedOrderType === OrderType.MANUAL) return templateTypeMapper[OrderType.MANUAL];
  if (isNomenclatureV3) return HOMOLOGAR_TYPE;
  return `${templateType}-${resolvedOrderType}`;
};

/**
 * Derives the real ProcedureType from a template's type string.
 * e.g. "order-imaging" → "imaging"
 */
export const procedureTypeFromTemplateType = (templateType) => {
  if (templateType === `order-${OrderType.IMAGING}`) return OrderType.IMAGING;
  if (templateType === `order-${OrderType.LABORATORY}`) return OrderType.LABORATORY;
  return null;
};

export const getTemplateCreationErrorMessage = (error) => {
  const isUniqueError = _.chain(error)
    .get("response.data.message")
    .includes("template already exists")
    .value();

  if (isUniqueError) return "Ya existe una plantilla con este nombre";

  return "No se pudo crear la plantilla";
};

export const NameHelperText = () => (
  <span>Debe ser único. Por ejemplo "RMN Cerebro simple"</span>
);

export const exceedsMaxItems = (maxItems, selectedItems) =>
  maxItems !== null && selectedItems.length > maxItems;
export const setMaxItemsErrorMessage = (maxItems, setError) =>
  setError(`Las plantillas de texto pueden tener hasta ${maxItems} elementos.`);
export const hasReachedMaxItems = (maxItems, selectedItems) =>
  maxItems !== null && selectedItems.length >= maxItems;

/**
 * DEV-1687: a mixed (v3) template can hold both imaging and laboratory items —
 * see capForProcedureType below for how each type's cap works and how they
 * interact once mixed. Items without their own procedureType (legacy free-text
 * items) fall back to imaging's cap, the more restrictive one.
 */
export const resolveItemProcedureType = (item) => item.procedureType ?? OrderType.IMAGING;

const countItemsOfType = (selectedItems, procedureType) =>
  selectedItems.filter((item) => resolveItemProcedureType(item) === procedureType).length;

/**
 * How many cards a type's own items would need, worst case: laboratory always
 * merges into exactly 1 card no matter how many items it has; imaging has no
 * such guarantee (a health center can configure any `imagingMaxItemsPerCard`,
 * unknown here), so 1 item is conservatively assumed to need 1 card.
 */
const cardsNeededForType = (procedureType, selectedItems) =>
  procedureType === OrderType.LABORATORY
    ? (countItemsOfType(selectedItems, procedureType) > 0 ? 1 : 0)
    : countItemsOfType(selectedItems, procedureType);

export const capForProcedureType = (method, procedureType, selectedItems = []) => {
  const rawCap = templateMaxItems[method][procedureType];

  // Only a TEXT-method template applies ALL of its items at once, automatically
  // (a SET template lets the doctor pick a subset via checkboxes when applying
  // it, so its items never all have to fit into one application together).
  // Mixing types in a TEXT template matters: the OTHER type present claims its
  // own card(s) too, and the total can't exceed MAX_ORDERS or the template
  // could never be fully applied, even to a brand-new empty order.
  if (method !== TemplateType.TEXT) return rawCap;

  if (procedureType === OrderType.LABORATORY) {
    // Laboratory's card cost is a step function, not linear with its item count
    // (1 card whether it holds 1 item or 50) — so its item count is never
    // itself the constraint. The only question is whether there's room for
    // that ONE guaranteed card at all: once at least one laboratory item is
    // already in, that card is already "paid for" and more can be added for
    // free (cap stays unlimited); only the very FIRST laboratory item can be
    // blocked, and only if imaging has already claimed every available card.
    // Note: intentionally ignores `rawCap` — laboratory's flat cap
    // (`templateMaxItems[method][LABORATORY]`) is always null today, since its
    // business limit is expressed purely via MAX_ORDERS, not an item count. If
    // that ever changes, this branch needs to fold `rawCap` in too.
    const alreadyHasLaboratory = countItemsOfType(selectedItems, OrderType.LABORATORY) > 0;
    const imagingCards = cardsNeededForType(OrderType.IMAGING, selectedItems);
    return alreadyHasLaboratory || imagingCards < MAX_ORDERS ? null : 0;
  }

  // Imaging: 1 card per item (worst case, see cardsNeededForType) — laboratory's
  // card cost is a fixed 1 once any laboratory item is present, so imaging's
  // ceiling shrinks by exactly that when mixed.
  const laboratoryCards = cardsNeededForType(OrderType.LABORATORY, selectedItems);
  if (laboratoryCards === 0) return rawCap;

  const budgetCap = MAX_ORDERS - laboratoryCards;
  return rawCap === null ? budgetCap : Math.min(rawCap, budgetCap);
};

export const hasReachedMaxItemsForType = (method, procedureType, selectedItems) => {
  const cap = capForProcedureType(method, procedureType, selectedItems);
  return cap !== null && countItemsOfType(selectedItems, procedureType) >= cap;
};

/** Returns the first procedureType whose count exceeds its cap, or null if none does. */
export const getExceededProcedureType = (method, selectedItems) => {
  const procedureTypes = _.uniq(selectedItems.map(resolveItemProcedureType));
  return (
    procedureTypes.find((procedureType) => {
      const cap = capForProcedureType(method, procedureType, selectedItems);
      return cap !== null && countItemsOfType(selectedItems, procedureType) > cap;
    }) ?? null
  );
};
